/**
 * @module NotificationPreferencesPage
 * @package @geenius/notifications-solidjs
 * @description Renders the full-page notification preference settings screen
 * for the Tailwind SolidJS variant.
 */

import { FadeIn } from '@geenius/motion/solidjs'
import type {
  NotificationChannel,
  NotificationPreferences,
  NotificationType,
} from '@geenius/notifications/shared'
import {
  buildNotificationsSeoMeta,
  DEFAULT_CHANNEL_PREFS,
  DEFAULT_TYPE_PREFS,
} from '@geenius/notifications/shared'
import { SEOHead } from '@geenius/seo/solidjs'
import type { JSX } from 'solid-js'
import { For, Show } from 'solid-js'
import { NotificationPreferencesForm } from '../components/NotificationPreferencesForm'
import { createNotificationCopy } from '../lib/notificationCopy'
import { cn } from '../lib/utils'
import { createNotificationPreferences } from '../primitives/createNotificationPreferences'

/**
 * Props for the SolidJS notification preferences page.
 */
export interface NotificationPreferencesPageProps {
  preferences: NotificationPreferences | null | undefined
  onUpdate?: (
    channels?: Record<NotificationChannel, boolean>,
    types?: Record<NotificationType, boolean>,
    quietHours?: { start: string; end: string },
  ) => Promise<void>
  class?: string
}

const CHANNELS: NotificationChannel[] = ['in-app', 'email', 'push', 'sms']
const TYPES: NotificationType[] = [
  'info',
  'success',
  'warning',
  'error',
  'mention',
  'invite',
  'payment',
  'system',
]

/**
 * Renders the full-page notification preferences experience.
 *
 * @param props Page props containing current preferences and update callback.
 * @returns The preferences page markup.
 */
export function NotificationPreferencesPage(
  props: NotificationPreferencesPageProps,
): JSX.Element {
  const { t } = createNotificationCopy()
  const state = createNotificationPreferences(() => props.preferences, props.onUpdate)

  const resolvedPreferences = () =>
    state.preferences() ?? {
      userId: '',
      channels: { ...DEFAULT_CHANNEL_PREFS },
      types: { ...DEFAULT_TYPE_PREFS },
      quietHours: { start: '22:00', end: '07:00' },
    }

  const seoMeta = buildNotificationsSeoMeta({
    title: t('notifications.seo.preferencesTitle'),
    description: t('notifications.seo.preferencesDescription'),
    path: '/notifications/preferences',
    keywords: ['notifications', 'preferences', 'channels', 'quiet hours'],
  })

  return (
    <>
      <SEOHead meta={seoMeta} />
      <Show
        when={!state.isLoading()}
        fallback={
          <div class={cn('mx-auto max-w-2xl p-6', props.class)}>
            <For each={Array.from({ length: 8 })}>
              {() => (
                <div class="mb-2 h-10 animate-pulse rounded-lg bg-[var(--gn-notifications-skeleton)]" />
              )}
            </For>
          </div>
        }
      >
        <div class={cn('mx-auto max-w-2xl space-y-6 p-6', props.class)}>
          <FadeIn>
            <div>
              <h1 class="text-2xl font-bold text-foreground">
                {t('notifications.preferences.pageTitle')}
              </h1>
              <p class="mt-1 text-[13px] text-muted-foreground">
                {t('notifications.preferences.pageDescription')}
              </p>
            </div>
          </FadeIn>
          <FadeIn delay={80}>
            <NotificationPreferencesForm
              preferences={resolvedPreferences()}
              onUpdate={async (channels, types, quietHours) => {
                if (channels) {
                  for (const channel of CHANNELS) {
                    if (resolvedPreferences().channels[channel] !== channels[channel]) {
                      await state.toggleChannel(channel, channels[channel])
                    }
                  }
                }

                if (types) {
                  for (const type of TYPES) {
                    if (resolvedPreferences().types[type] !== types[type]) {
                      await state.toggleType(type, types[type])
                    }
                  }
                }

                if (quietHours) {
                  await state.setQuietHours(quietHours.start, quietHours.end)
                }
              }}
            />
          </FadeIn>
        </div>
      </Show>
    </>
  )
}
